import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Home, Bell, Shield, Users, Briefcase, FileCheck, 
  ClipboardCheck, Image, MessageSquare, BarChart3,
  ChevronRight, Loader2, CheckCircle2, XCircle, Eye,
  Calendar, AlertTriangle, TrendingUp, Clock, HelpCircle, DollarSign,
  Lock, Upload, Sparkles, Palette, UserCog, Search, Archive, Trash2, LogOut, Send, Phone, Link2, Plug, UserX, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import QuickActionBar from '../../components/QuickActionBar';
// Iter-250 — Community adoption widgets
import CommunityHighlights from '../../components/CommunityHighlights';
import CommunityIntroModal, { useCommunityIntroFlag } from '../../components/CommunityIntroModal';
import { useNoOtherDialogOpen } from '../../hooks/useNoOtherDialogOpen';
// Iter-220q — GeocodeCoverageChip moved out of /admin/home into the new
// "Data Maintenance" tab on /admin/operations. Background system metrics
// don't belong on the primary dashboard.
import HelpMenu from '../../components/HelpMenu';
import LiveOpsHealthWidget from '../../components/LiveOpsHealthWidget';
import Lock24hBanner from '../../components/Lock24hBanner';
import AdminBackendMisconfigAlert from '../../components/AdminBackendMisconfigAlert';
import SystemHealthCard from '../../components/SystemHealthCard';  // Iter-222
import OperationsSummaryTile from '../../components/OperationsSummaryTile';  // Iter-225L
import { AIInsightsTile } from '../../components/AIInsightsTile';  // Iter-235c
import useIntegrationsHealth from '../../hooks/useIntegrationsHealth';

import { formatDateMonthDay } from '../../utils/timeFormat';
const AdminHome = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const { user, hasAdminPermission, isSuperAdmin } = useAuth();
  // Iter-220g — surface integration-status badges on the Home Quick
  // Action tile + inline card so the next-action ("re-authorize Xero")
  // is visible without opening the page.
  const integrationsHealth = useIntegrationsHealth();
  const [loading, setLoading] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [moderating, setModerating] = useState({});
  const [revenueSnapshot, setRevenueSnapshot] = useState(null);
  const [adminPermissions, setAdminPermissions] = useState(null);
  const [hideTestEvents, setHideTestEvents] = useState(() => {
    return localStorage.getItem('admin_hide_test_events') === '1';
  });
  const [notificationCounts, setNotificationCounts] = useState({
    pending_approval: 0,
    pending_custom_requests: 0,
    total_urgent: 0
  });
  const [clockInReviewsCount, setClockInReviewsCount] = useState(0);
  // Iter-221+ — Client Approvals discoverability. Surfaces pending +
  // high-priority + missing-intake counts directly on /admin/home next
  // to Creator Approvals so admins never have to hunt through tabs.
  const [clientApprovalCounts, setClientApprovalCounts] = useState({
    pending: 0, high_priority: 0, missing_intake: 0,
  });

  useEffect(() => {
    fetchHomeData();
    fetchPendingPosts();
    fetchAdminPermissions();
    fetchNotificationCounts();
    fetchClockInReviewsCount();
    fetchClientApprovalCounts();
  }, []);
  
  // Only fetch revenue if user has financial permission
  useEffect(() => {
    if (adminPermissions?.can_view_financial) {
      fetchRevenueSnapshot();
    }
  }, [adminPermissions]);
  
  const fetchNotificationCounts = async () => {
    try {
      const res = await api.get('/admin/notifications/counts');
      setNotificationCounts(res.data);
    } catch (e) {
      console.error('Failed to load notification counts:', e);
    }
  };

  const fetchClockInReviewsCount = async () => {
    try {
      const res = await api.get('/attendance/clock-in-reviews/count');
      setClockInReviewsCount(res.data?.pending_reviews || 0);
    } catch (e) {
      console.error('Failed to load clock-in reviews count:', e);
    }
  };

  // Iter-221+ — Fetch consolidated client-approval counts. The
  // /admin/client-approvals endpoint returns a `counts` dict with
  // `pending / approved / rejected / converted / missing_intake /
  // high_priority` (Iter-220b). We surface 3 of those on the home card.
  const fetchClientApprovalCounts = async () => {
    try {
      const res = await api.get('/admin/client-approvals?status=pending');
      const c = res.data?.counts || {};
      setClientApprovalCounts({
        pending: Number(c.pending) || 0,
        high_priority: Number(c.high_priority) || 0,
        missing_intake: Number(c.missing_intake) || 0,
      });
    } catch (e) {
      console.error('Failed to load client approval counts:', e);
    }
  };

  const fetchAdminPermissions = async () => {
    try {
      const res = await api.get('/admin/permissions');
      setAdminPermissions(res.data.permissions);
    } catch (e) {
      console.error('Failed to load admin permissions:', e);
    }
  };

  const fetchHomeData = async () => {
    try {
      const res = await api.get('/home');
      setHomeData(res.data);
    } catch (e) {
      toast.error('Failed to load home data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingPosts = async () => {
    try {
      const res = await api.get('/feed/admin/pending');
      setPendingPosts(res.data || []);
    } catch (e) {
      console.error('Failed to load pending posts:', e);
    }
  };

  const fetchRevenueSnapshot = async () => {
    try {
      const res = await api.get('/analytics/revenue/snapshot');
      setRevenueSnapshot(res.data);
    } catch (e) {
      console.error('Failed to load revenue snapshot:', e);
    }
  };

  const handleModerate = async (postId, action) => {
    setModerating(prev => ({ ...prev, [postId]: true }));
    try {
      await api.patch(`/feed/admin/${postId}/moderate`, { action });
      toast.success(`Post ${action}d`);
      setPendingPosts(posts => posts.filter(p => p.id !== postId));
      fetchHomeData(); // Refresh counts
    } catch (e) {
      toast.error(`Failed to ${action} post`);
    } finally {
      setModerating(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Iter-292g — Community intro modal. See CommunityIntroModal.js + the
  // useNoOtherDialogOpen gate: don't open while a Radix dialog is up.
  const { seen: introSeen } = useCommunityIntroFlag();
  const [showCommunityIntro, setShowCommunityIntro] = useState(false);
  const safeToOpenCommunity = useNoOtherDialogOpen(!introSeen && !showCommunityIntro, 'community-intro-modal');
  useEffect(() => {
    if (introSeen) {
      setShowCommunityIntro(false);
      return;
    }
    if (!safeToOpenCommunity) return;
    const t = setTimeout(() => setShowCommunityIntro(true), 2500);
    return () => clearTimeout(t);
  }, [introSeen, safeToOpenCommunity]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#03DFFC]" />
      </div>
    );
  }

  const { moderation_queue, pending_approvals, quiz_stats, platform_metrics, recent_pending_posts, recent_events } = homeData || {};

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="admin-home">
      <CommunityIntroModal open={showCommunityIntro} onClose={() => setShowCommunityIntro(false)} />
      {/* Header */}
      <header className="bg-gray-50 border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Shield className="w-5 h-5 text-[#03DFFC] shrink-0" />
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Admin Dashboard</h1>
          </div>
          {/* Iter-213+ P0 — Header action group MUST shrink + scroll on mobile.
              Previously 9 icon buttons in a no-wrap flex blew past the 390px
              viewport and forced the whole dashboard into horizontal scroll.
              Solution: allow the group to scroll horizontally INSIDE itself so
              page width stays clamped, and keep buttons compact at sub-sm. */}
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto max-w-full" data-testid="admin-home-actions">
            <HelpMenu />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/profile')}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              data-testid="admin-home-profile-btn"
            >
              <UserCog className="w-5 h-5 mr-1" />
              <span className="hidden sm:inline text-sm">My Profile</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/payouts')}
              className="text-gray-600 hover:text-emerald-700 hover:bg-emerald-50"
              data-testid="admin-home-payouts-btn"
            >
              <DollarSign className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Payouts</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/payment-flow')}
              className="text-gray-600 hover:text-cyan-700 hover:bg-cyan-50"
              data-testid="admin-home-payment-flow-btn"
            >
              <DollarSign className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Payment Flow</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/auto-payouts')}
              className="text-gray-600 hover:text-emerald-700 hover:bg-emerald-50"
              data-testid="admin-home-auto-payouts-btn"
              title="Auto Payouts — kill switch + dry-run + audit log"
            >
              <Lock className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Auto Pay</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/broadcast')}
              className="text-gray-600 hover:text-cyan-700 hover:bg-cyan-50"
              data-testid="admin-home-broadcast-btn"
            >
              <Send className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Broadcast</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/booking-requests')}
              className="text-gray-600 hover:text-pink-700 hover:bg-pink-50"
              data-testid="admin-home-booking-requests-btn"
              title="Direct booking requests from /book/<slug> share links"
            >
              <Link2 className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Bookings</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/phone-fixer')}
              className="text-gray-600 hover:text-amber-700 hover:bg-amber-50"
              data-testid="admin-home-phone-fixer-btn"
              title="Fix unusable phone numbers that skip SMS broadcasts"
            >
              <Phone className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Phones</span>
            </Button>
            {/* Iter-220m — Incomplete Onboarding Queue */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/incomplete-onboarding')}
              className="text-gray-600 hover:text-amber-700 hover:bg-amber-50"
              data-testid="admin-home-incomplete-onboarding-btn"
              title="Find users who signed up but never finished onboarding"
            >
              <UserX className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Stuck Signups</span>
            </Button>
            {/* Iter-220n — Multi-Slot Events */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/multi-slot-events')}
              className="text-gray-600 hover:text-cyan-700 hover:bg-cyan-50"
              data-testid="admin-home-multi-slot-btn"
              title="Create one event with multiple independent shift slots"
            >
              <Layers className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Multi-Slot</span>
            </Button>
            {/* Iter-220g — Integrations Quick Action. Always visible to
                every admin (no permission gate). Shows an amber dot if
                any integration needs attention (e.g. Xero re-auth). */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/integrations')}
              className="text-gray-600 hover:text-violet-700 hover:bg-violet-50 relative"
              data-testid="admin-home-integrations-btn"
              title="Xero accounting · Yardstik background checks"
            >
              <Plug className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Integrations</span>
              {integrationsHealth.alertCount > 0 && (
                <span
                  className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                    integrationsHealth.worstSeverity === 'warn'
                      ? 'bg-amber-500'
                      : 'bg-slate-400'
                  }`}
                  data-testid="admin-home-integrations-alert-dot"
                  aria-label={`${integrationsHealth.alertCount} integration needs attention`}
                />
              )}
            </Button>
            <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900 hover:bg-gray-100" data-testid="admin-home-notifications-btn">
              <Bell className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-gray-600 hover:text-red-600 hover:bg-red-50"
              data-testid="admin-home-logout-btn"
            >
              <LogOut className="w-5 h-5 sm:mr-1" />
              <span className="hidden sm:inline text-sm">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Iter-192.2 — Lock in next 24h sticky banner for admin ops. */}
      <Lock24hBanner />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Iter-221 — Admin-only backend misconfig alert (replaces public banner). */}
        <AdminBackendMisconfigAlert />
        {/* Iter-224g — System Health moved to the bottom of the dashboard so
            operational content (staffing, payouts, invoicing, closeout) leads
            the page. Full diagnostics live on the dedicated /admin/system-health
            page. */}
        {/* Live Ops Health — single-glance dispatch scoreboard */}
        <div className="mb-6">
          <LiveOpsHealthWidget />
        </div>

        {/* Iter-225L — Operations Summary tile (event lifecycle scoreboard) */}
        <div className="mb-6">
          <OperationsSummaryTile />
        </div>

        {/* Iter-235c — AI Event Insights tile */}
        <div className="mb-6">
          <AIInsightsTile />
        </div>

        {/* Top Stats - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md hover:border-[#03DFFC] transition-all"
            onClick={() => navigate('/admin/users?filter=talent')}
            data-testid="metric-active-talent"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Talent</p>
                  <p className="text-2xl font-bold text-gray-900">{platform_metrics?.active_talent || 0}</p>
                </div>
                <Users className="w-8 h-8 text-[#03DFFC]" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Click to view all →</p>
            </CardContent>
          </Card>
          <Card 
            className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md hover:border-green-500 transition-all"
            onClick={() => navigate('/admin/users?filter=client')}
            data-testid="metric-active-clients"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Clients</p>
                  <p className="text-2xl font-bold text-gray-900">{platform_metrics?.active_clients || 0}</p>
                </div>
                <Briefcase className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Click to view all →</p>
            </CardContent>
          </Card>
          <Card 
            className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md hover:border-amber-500 transition-all"
            onClick={() => navigate('/admin/shifts?status=open')}
            data-testid="metric-open-gigs"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Open Gigs</p>
                  <p className="text-2xl font-bold text-gray-900">{platform_metrics?.open_gigs || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-amber-500" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Click to view all →</p>
            </CardContent>
          </Card>
          <Card 
            className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md hover:border-orange-400 transition-all"
            onClick={() => navigate('/admin/bookings')}
            data-testid="metric-weekly-bookings"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Weekly Bookings</p>
                  <p className="text-2xl font-bold text-gray-900">{platform_metrics?.weekly_bookings || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-orange-500" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Click to view all →</p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Snapshot - FINANCE/SUPER ADMIN ONLY
            Iter-133n (2026-05-01) — dashboard now shows WEEKLY REVENUE ONLY.
            Open receivables / overdue / upcoming / aging / stale all moved
            to the dedicated Revenue page (/admin/revenue). */}
        {adminPermissions?.can_view_financial && revenueSnapshot && (
          <div className="mb-6" data-testid="revenue-snapshot">
            <button
              type="button"
              onClick={() => navigate('/admin/revenue')}
              className="text-left w-full sm:max-w-md"
              data-testid="rev-card-week"
            >
              <Card className="bg-gradient-to-br from-[#03DFFC]/5 to-white border-[#03DFFC]/20 shadow-sm hover:shadow-md hover:border-[#03DFFC]/40 transition-all cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Weekly Revenue</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">${revenueSnapshot.weekly?.total?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {revenueSnapshot.weekly?.count || 0} paid invoice{(revenueSnapshot.weekly?.count || 0) === 1 ? '' : 's'}
                        <span className="ml-2 text-[#03DFFC] hover:underline">View full revenue →</span>
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[#03DFFC]/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#03DFFC]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </button>
          </div>
        )}
        
        {/* Restricted Notice for Non-Financial Admins */}
        {adminPermissions && !adminPermissions.can_view_financial && (
          <div className="mb-6 p-4 bg-gray-100 border border-gray-200 rounded-lg flex items-center gap-3">
            <Lock className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-700">Financial data restricted</p>
              <p className="text-xs text-gray-500">Contact Finance Admin or Super Admin for financial reports</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Queues & Approvals */}
          <div className="space-y-4">
            {/* Moderation Queue */}
            <Card className={`bg-gray-50 border-gray-200 ${moderation_queue?.total > 0 ? 'border-l-4 border-l-orange-500' : ''}`}>
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-orange-400" />
                  Moderation Queue
                </CardTitle>
                {moderation_queue?.total > 0 && (
                  <Badge className="bg-orange-500 text-gray-900">{moderation_queue.total}</Badge>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Pending Posts</span>
                  <span className="text-gray-900 font-medium">{moderation_queue?.pending_posts || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Pending Media</span>
                  <span className="text-gray-900 font-medium">{moderation_queue?.pending_media || 0}</span>
                </div>
                {moderation_queue?.total > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2 border-gray-600 text-gray-600 hover:bg-gray-100"
                    onClick={() => navigate('/admin/moderation')}
                  >
                    Review Queue
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Pending Approvals */}
            <Card className={`bg-gray-50 border-gray-200 ${pending_approvals?.total > 0 ? 'border-l-4 border-l-cyan-500' : ''}`}>
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-[#03DFFC]" />
                  Pending Approvals
                </CardTitle>
                {pending_approvals?.total > 0 && (
                  <Badge className="bg-[#03DFFC] text-gray-900">{pending_approvals.total}</Badge>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {/* Iter-246b — IDV review row removed from admin home per Mel.
                    Reviewers can still navigate to /admin/idv-review directly. */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Premium Requests</span>
                  <span className="text-gray-900 font-medium">{pending_approvals?.premium_requests || 0}</span>
                </div>
              </CardContent>
            </Card>

            {/* Clock-In Reviews — Iter-157m */}
            <Card
              className={`bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all ${clockInReviewsCount > 0 ? 'border-l-4 border-l-amber-500' : ''}`}
              onClick={() => navigate('/admin/clock-in-reviews')}
              data-testid="clock-in-reviews-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-500" />
                  Clock-In Reviews
                </CardTitle>
                {clockInReviewsCount > 0 && (
                  <Badge className="bg-amber-500 text-white" data-testid="clock-in-reviews-badge">{clockInReviewsCount}</Badge>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-2">
                  Talent who failed GPS or selfie checks at clock-in. Approve or reject manually.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="open-clock-in-reviews-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/clock-in-reviews'); }}
                >
                  {clockInReviewsCount > 0 ? `Review ${clockInReviewsCount} Pending` : 'View Queue'}
                </Button>
              </CardContent>
            </Card>

            {/* Ratings Moderation — Iter-163 */}
            <Card
              className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate('/admin/ratings/moderation')}
              data-testid="ratings-moderation-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-rose-500" />
                  Ratings Moderation
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-2">
                  Flagged or pending reviews. Hide / remove abuse, retaliation, or off-topic feedback.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-rose-500 hover:bg-rose-600 text-white border-rose-500 hover:border-rose-600"
                  data-testid="open-ratings-moderation-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/ratings/moderation'); }}
                >
                  Open queue
                </Button>
              </CardContent>
            </Card>

            {/* Iter-168 Phase 3 — Profile Quality Report */}
            <Card
              className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate('/admin/profile-quality')}
              data-testid="profile-quality-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-cyan-500" />
                  Profile Quality
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-2">
                  Top profiles, missing photos / bios / IDV. Boost marketplace quality.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white border-cyan-500 hover:border-cyan-600"
                  data-testid="open-profile-quality-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/profile-quality'); }}
                >
                  Open report
                </Button>
              </CardContent>
            </Card>

            {/* Iter-191 — Markets management */}
            <Card
              className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate('/admin/markets')}
              data-testid="markets-admin-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-500" />
                  Markets
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-2">
                  Manage metro regions (Atlanta, DC-MD-VA, etc.) and the talent request queue.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500 hover:border-emerald-600"
                  data-testid="open-markets-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/markets'); }}
                >
                  Manage markets
                </Button>
              </CardContent>
            </Card>

            {/* Iter-171 — AI Talent management (admin-only seeding) */}
            <Card
              className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate('/admin/ai-talent')}
              data-testid="ai-talent-admin-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-fuchsia-500" />
                  AI Talent
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-2">
                  Curate AI ambassadors and virtual creators for hybrid campaigns.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white border-0"
                  data-testid="open-ai-talent-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/ai-talent'); }}
                >
                  Manage AI talent
                </Button>
              </CardContent>
            </Card>

            {/* Iter-221+ — Client Approvals discoverability card.
                Placed next to Creator Approvals / AI Talent / Markets
                so admins immediately see incoming company signups
                without hunting through the dashboard. Counts pulled
                from /api/admin/client-approvals?status=pending. */}
            <Card
              className={`bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all ${
                clientApprovalCounts.pending > 0 ? 'border-l-4 border-l-cyan-500' : ''
              }`}
              onClick={() => navigate('/admin/client-approvals?status=pending')}
              data-testid="client-approvals-home-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-cyan-600" />
                  Client Approvals
                </CardTitle>
                {clientApprovalCounts.pending > 0 && (
                  <Badge
                    className="bg-rose-500 text-white"
                    data-testid="client-approvals-home-pending-badge"
                  >
                    {clientApprovalCounts.pending} Pending
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                <p className="text-xs text-gray-500">
                  Review new company / agency signups, intake answers, and
                  account-conversion requests.
                </p>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div className="rounded border border-gray-200 bg-white p-1.5 text-center">
                    <div
                      className="text-base font-semibold text-gray-900"
                      data-testid="client-approvals-home-count-pending"
                    >
                      {clientApprovalCounts.pending}
                    </div>
                    <div className="text-gray-500">Pending</div>
                  </div>
                  <div className="rounded border border-gray-200 bg-white p-1.5 text-center">
                    <div
                      className="text-base font-semibold text-amber-700"
                      data-testid="client-approvals-home-count-high-priority"
                    >
                      {clientApprovalCounts.high_priority}
                    </div>
                    <div className="text-gray-500">High priority</div>
                  </div>
                  <div className="rounded border border-gray-200 bg-white p-1.5 text-center">
                    <div
                      className="text-base font-semibold text-rose-700"
                      data-testid="client-approvals-home-count-missing-intake"
                    >
                      {clientApprovalCounts.missing_intake}
                    </div>
                    <div className="text-gray-500">Missing intake</div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white border-cyan-600 hover:border-cyan-700"
                  data-testid="open-client-approvals-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/client-approvals?status=pending'); }}
                >
                  Open queue
                </Button>
              </CardContent>
            </Card>

            {/* Iter-220g — Integrations card. Mounted next to Client
                Approvals so Xero / Yardstik state is one tap away. Has
                an amber "Xero · Re-auth" badge or slate "Yardstik ·
                Sandbox" badge whenever a connection needs attention. */}
            <Card
              className={`bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all ${
                integrationsHealth.xero.needsReauth ? 'border-l-4 border-l-amber-500' : ''
              }`}
              onClick={() => navigate('/admin/integrations')}
              data-testid="integrations-home-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Plug className="w-4 h-4 text-violet-600" />
                  Integrations
                </CardTitle>
                {integrationsHealth.xero.needsReauth && (
                  <Badge
                    className="bg-amber-500 text-white"
                    data-testid="integrations-home-xero-reauth-badge"
                  >
                    Xero · Re-auth
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                <p className="text-xs text-gray-500">
                  Manage Xero accounting + Yardstik background checks.
                </p>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div
                    className={`rounded border p-1.5 ${
                      integrationsHealth.xero.needsReauth
                        ? 'bg-amber-50 border-amber-200'
                        : integrationsHealth.xero.configured
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-white border-gray-200'
                    }`}
                    data-testid="integrations-home-xero-status"
                  >
                    <div className="text-[11px] font-medium text-gray-900">Xero</div>
                    <div className="text-[10px] text-gray-600">
                      {integrationsHealth.loading
                        ? 'Checking…'
                        : integrationsHealth.xero.needsReauth
                          ? 'Re-authorization required'
                          : integrationsHealth.xero.configured
                            ? 'Connected'
                            : 'Not connected'}
                    </div>
                  </div>
                  <div
                    className={`rounded border p-1.5 ${
                      !integrationsHealth.yardstik.enabled && !integrationsHealth.loading
                        ? 'bg-rose-50 border-rose-200'
                        : integrationsHealth.yardstik.mockMode
                          ? 'bg-slate-50 border-slate-200'
                          : integrationsHealth.yardstik.enabled
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-white border-gray-200'
                    }`}
                    data-testid="integrations-home-yardstik-status"
                  >
                    <div className="text-[11px] font-medium text-gray-900">Yardstik</div>
                    <div className="text-[10px] text-gray-600">
                      {integrationsHealth.loading
                        ? 'Checking…'
                        : !integrationsHealth.yardstik.enabled
                          ? 'Not connected'
                          : integrationsHealth.yardstik.mockMode
                            ? 'Sandbox mode'
                            : 'Connected'}
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white border-violet-600 hover:border-violet-700"
                  data-testid="open-integrations-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/integrations'); }}
                >
                  Manage integrations
                </Button>
              </CardContent>
            </Card>

            {/* Iter-172 — AI Creator approvals (creator economy moderation) */}
            <Card
              className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate('/admin/ai-creators')}
              data-testid="ai-creators-admin-card"
            >
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  Creator Approvals
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-2">
                  Approve / reject AI creator signups and avatar submissions.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:opacity-90 text-white border-0"
                  data-testid="open-ai-creators-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/ai-creators'); }}
                >
                  Open queue
                </Button>
              </CardContent>
            </Card>

            {/* Quiz Stats */}
            <Card className="bg-gray-50 border-gray-200">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-green-500" />
                  Quiz Results (7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-center justify-around">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{quiz_stats?.total_attempts_7d || 0}</p>
                    <p className="text-xs text-gray-500">Attempts</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{quiz_stats?.passed || 0}</p>
                    <p className="text-xs text-gray-500">Passed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{quiz_stats?.failed || 0}</p>
                    <p className="text-xs text-gray-500">Failed</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3 border-gray-600 text-gray-600 hover:bg-gray-100"
                  onClick={() => navigate('/quizzes/manage')}
                >
                  View Results
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions — drag-and-drop reordering, permission-aware */}
            <QuickActionBar role="admin" permissions={adminPermissions} />
            {/* Iter-250 — Community Highlights ribbon */}
            <CommunityHighlights />
          </div>

          {/* Center & Right - Feed Moderation & Events */}
          <div className="lg:col-span-2 space-y-4">
            {/* Feed Moderation Preview */}
            <Card className="bg-gray-50 border-gray-200">
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-orange-400" />
                  Posts Awaiting Approval
                </CardTitle>
                {pendingPosts.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900" onClick={() => navigate('/admin/community-moderation')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {pendingPosts.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
                    <p className="text-gray-500">All caught up! No pending posts.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingPosts.slice(0, 3).map(post => (
                      <div key={post.id} className="p-3 bg-gray-100 rounded-lg">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">{post.author_name}</span>
                              <Badge variant="outline" className="text-xs text-gray-500 border-gray-500">
                                {post.post_type}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-2">{post.content}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDateMonthDay(post.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-green-600 hover:bg-green-400/20"
                              onClick={() => handleModerate(post.id, 'approve')}
                              disabled={moderating[post.id]}
                            >
                              {moderating[post.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-500 hover:bg-red-400/20"
                              onClick={() => handleModerate(post.id, 'reject')}
                              disabled={moderating[post.id]}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-gray-500 hover:bg-gray-600"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Events Activity */}
            <Card className="bg-gray-50 border-gray-200">
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2 min-w-0">
                  <Calendar className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="truncate">Recent Event Activity</span>
                </CardTitle>
                <div className="flex items-center gap-1.5 shrink-0">
                  {isSuperAdmin() && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                      onClick={async () => {
                        if (!window.confirm('Archive all venues/users containing "test/demo/sample/example"? (Soft delete — can be restored.)')) return;
                        try {
                          const res = await api.post('/admin/cleanup/archive-test-data');
                          toast.success(`Cleanup done: ~${res.data.users_archived_matching} users matched`);
                          fetchHomeData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Cleanup failed');
                        }
                      }}
                      data-testid="bulk-archive-test-data"
                      title="Archive all test/demo/sample venues + users"
                    >
                      Clean Test Data
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 px-2"
                    onClick={() => {
                      const next = !hideTestEvents;
                      setHideTestEvents(next);
                      localStorage.setItem('admin_hide_test_events', next ? '1' : '0');
                    }}
                    data-testid="toggle-hide-test-events"
                  >
                    {hideTestEvents ? 'Show Test' : 'Hide Test'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-4 pb-4">
                {(() => {
                  const events = (recent_events || []).filter((e) => {
                    if (!hideTestEvents) return true;
                    const v = (e.venue_name || '').toLowerCase();
                    const c = (e.company_name || '').toLowerCase();
                    return !/test|demo|sample|example/.test(v + ' ' + c);
                  });
                  if (events.length === 0) {
                    return <p className="text-center text-gray-500 py-4 text-sm">No recent events</p>;
                  }
                  return (
                    <div className="space-y-2">
                      {events.map(event => (
                        <div
                          key={event.gig_id || event.id}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-gray-100 rounded-lg"
                          data-testid="recent-event-row"
                        >
                          <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/admin/shifts/${event.gig_id || event.id}`)}>
                            <p className="text-sm font-medium text-gray-900 truncate">{event.venue_name || '(no venue name)'}</p>
                            <p className="text-xs text-gray-500 truncate">{event.company_name}</p>
                          </div>
                          <div className="flex items-center flex-wrap gap-2">
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                event.status === 'completed' ? 'text-green-600 border-green-400' :
                                event.status === 'open' ? 'text-[#03DFFC] border-cyan-400' :
                                'text-gray-500 border-gray-500'
                              }`}
                            >
                              {event.status}
                            </Badge>
                            {event.media_count > 0 && (
                              <Badge className="bg-cyan-500 text-white text-xs">
                                <Image className="w-3 h-3 mr-1" />
                                {event.media_count}
                              </Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {formatDateMonthDay(event.shift_date)}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 border-amber-200 text-amber-700 hover:bg-amber-50"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await api.patch(`/admin/shifts/${event.gig_id || event.id}/archive`);
                                  toast.success('Event archived');
                                  fetchHomeData();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to archive');
                                }
                              }}
                              data-testid={`archive-event-${event.gig_id || event.id}`}
                              title="Archive this event (hides from lists; data preserved)"
                            >
                              <Archive className="w-3 h-3" />
                            </Button>
                            {isSuperAdmin() && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 border-red-300 text-red-700 hover:bg-red-50"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm('Permanently delete this event? This cannot be undone.')) return;
                                  try {
                                    const res = await api.delete(`/admin/shifts/${event.gig_id || event.id}`);
                                    if (res.data?.fallback === 'archived') {
                                      toast.warning('Hard delete blocked — archived instead');
                                    } else {
                                      toast.success('Event deleted permanently');
                                    }
                                    fetchHomeData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to delete');
                                  }
                                }}
                                data-testid={`delete-event-${event.gig_id || event.id}`}
                                title="Permanently delete (Super Admin only)"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Two-Way Ratings Summary */}
            <Card className="bg-gray-50 border-gray-200">
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#03DFFC]" />
                  Ratings Overview
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900" onClick={() => navigate('/admin/ratings/all')}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Client → Talent</p>
                    <p className="text-lg font-bold text-gray-900">View Ratings</p>
                    <p className="text-xs text-gray-500">Performance reviews from clients</p>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Talent → Client</p>
                    <p className="text-lg font-bold text-gray-900">View Ratings</p>
                    <p className="text-xs text-gray-500">Experience feedback from talent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Iter-224g — System Health (moved to the bottom of the dashboard).
            Stays collapsed by default; clicking "View Full Diagnostics" opens
            the dedicated /admin/system-health page. */}
        <div className="mt-6" data-testid="dashboard-system-health-section">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">
              Background System Status
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate('/admin/system-health');
              }}
              className="inline-flex items-center text-xs text-cyan-600 hover:text-cyan-700 hover:underline h-7 px-2"
              data-testid="view-full-system-health-btn"
            >
              View Full Diagnostics <ChevronRight className="w-3 h-3 ml-1" />
            </button>
          </div>
          <SystemHealthCard />
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
